import {
  clockAtArcFraction,
  cloneSpeedProfile,
  normalizeSpeedProfile,
  pathProgress,
  trimSpeedProfile,
  type CameraSpeedProfile,
} from "@/lib/location-scene-3d/camera-speed"

export type CameraPathSample = {
  t: number
  pos: [number, number, number]
  yaw: number
  pitch: number
  fov: number
}

export type PathVec3 = [number, number, number]

/** Точка, в которую смотрит кадр, или объект сцены и последняя известная точка. */
export type PathLookTarget = {
  t_ms: number
  point?: PathVec3
  object_uid?: string
}

export type PathLook =
  | { mode: "tangent" }
  | { mode: "observe"; object_uid: string }
  | { mode: "recorded"; look_at?: PathVec3; look_targets?: PathLookTarget[] }

/** Версия 2 помнит «Вдоль» при живой дорожке взгляда. Версия 3 хранит ключи взгляда по доле пути, не по часам. */
export const CAMERA_PATH_VERSION = 3

/** `tangent: null` — автоизгиб. Вектор — симметричная ручка. */
export type PathPoint = {
  pos: PathVec3
  tangent: PathVec3 | null
}

export type CameraPathAuthoring = {
  smooth: boolean
  look: PathLook
  points: PathPoint[]
}

/** Взгляд по доле пути: t — миллисекунды равномерного пролёта, не часы ролика. Не рычаг и не режим «Вдоль» / «Наблюдение». */
export type PathLookKey = {
  t: number
  yaw: number
  pitch: number
}

export type PathEditUi = {
  active: boolean
  draw: boolean
  observeArmed: boolean
  smooth: boolean
  durationMs: number
  look: PathLook
  /** На пролёте есть дорожка взгляда. */
  lookRecorded: boolean
  /** Идёт запись только взгляда. В camera_path не пишется. */
  lookRecording: boolean
  /** Перезапись взгляда ждёт Пробел или клик. В camera_path не пишется. */
  lookRewriteArmed: boolean
  /** Проигрывание везёт вид и прячет траекторию. В camera_path не пишется. */
  lookThrough: boolean
  /** Правка пути этого объекта. null — режим камеры или покой. */
  objectPathUid: string | null
  /** Линия пути объекта без точек. null — обычные призраки. */
  previewObjectPathUid: string | null
}

export type CameraPath = {
  version: number
  duration_ms: number
  samples: CameraPathSample[]
  authoring?: CameraPathAuthoring
  look_keys?: PathLookKey[]
  /** Нет поля — равномерно по длине дуги, как до кривой скорости. */
  speed?: CameraSpeedProfile
}

export const MAX_PATH_SAMPLES = 2000
export const MAX_PATH_DURATION_MS = 30_000
export const SAMPLE_INTERVAL_MS = 50
/** Дальше этого шага кадр записи или точка авторства не бывают живыми. */
export const MAX_PATH_STEP_M = 500
/** Минимальный осмысленный диапазон обрезки. */
export const MIN_TRIM_SPAN_MS = 500

export function emptyCameraPath(): CameraPath {
  return { version: 1, duration_ms: 0, samples: [] }
}

export function cloneCameraPath(path: CameraPath): CameraPath {
  return {
    version: path.version,
    duration_ms: path.duration_ms,
    samples: path.samples.map((sample) => ({ ...sample, pos: [...sample.pos] as PathVec3 })),
    authoring: path.authoring
      ? {
          smooth: path.authoring.smooth,
          look: copyPathLook(path.authoring.look),
          points: path.authoring.points.map((point) => ({
            pos: [...point.pos] as PathVec3,
            tangent: point.tangent ? ([...point.tangent] as PathVec3) : null,
          })),
        }
      : undefined,
    look_keys: cloneLookKeys(path.look_keys),
    speed: path.speed ? cloneSpeedProfile(path.speed) : undefined,
  }
}

export function cameraPathsEqual(a: CameraPath, b: CameraPath): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

const PATH_HISTORY_LABEL = "locationsPage.scene3d.historyActions"

function speedChanged(prev: CameraPath, next: CameraPath): boolean {
  return JSON.stringify(prev.speed ?? null) !== JSON.stringify(next.speed ?? null)
}

function pointsMoved(prev: CameraPath, next: CameraPath): boolean {
  const a = prev.authoring?.points ?? []
  const b = next.authoring?.points ?? []
  return a.some((point, index) => {
    const other = b[index]
    if (!other) return false
    return point.pos.some((value, axis) => Math.abs(value - other.pos[axis]) > 1e-4)
  })
}

function tangentsChanged(prev: CameraPath, next: CameraPath): boolean {
  const a = prev.authoring?.points ?? []
  const b = next.authoring?.points ?? []
  return a.some((point, index) => JSON.stringify(point.tangent) !== JSON.stringify(b[index]?.tangent))
}

export function pathHistoryLabelKey(prev: CameraPath, next: CameraPath): string {
  const prevCount = prev.authoring?.points.length ?? 0
  const nextCount = next.authoring?.points.length ?? 0
  if (nextCount === 0 && next.samples.length === 0 && (prevCount > 0 || prev.samples.length > 0)) {
    return `${PATH_HISTORY_LABEL}.pathClear`
  }
  if (prevCount === 0 && nextCount >= 2 && prev.samples.length >= 2 && next.samples.length >= 2) {
    return `${PATH_HISTORY_LABEL}.pathRecord`
  }
  if (nextCount > prevCount) return `${PATH_HISTORY_LABEL}.pathAddPoint`
  if (nextCount < prevCount && nextCount > 0) return `${PATH_HISTORY_LABEL}.pathDeletePoint`
  if (nextCount > 0 && prevCount === nextCount) {
    if (pointsMoved(prev, next)) return `${PATH_HISTORY_LABEL}.pathMovePoint`
    if (tangentsChanged(prev, next)) return `${PATH_HISTORY_LABEL}.pathBend`
    if (JSON.stringify(prev.authoring?.look) !== JSON.stringify(next.authoring?.look)) {
      return `${PATH_HISTORY_LABEL}.pathLook`
    }
    if (prev.authoring?.smooth !== next.authoring?.smooth) return `${PATH_HISTORY_LABEL}.pathSmooth`
    if (prev.duration_ms !== next.duration_ms) return `${PATH_HISTORY_LABEL}.pathDuration`
    if (JSON.stringify(prev.look_keys ?? []) !== JSON.stringify(next.look_keys ?? [])) {
      return `${PATH_HISTORY_LABEL}.pathLook`
    }
    if (speedChanged(prev, next)) return `${PATH_HISTORY_LABEL}.pathSpeed`
  }
  if (nextCount === 0 && next.samples.length >= 2) {
    if (speedChanged(prev, next)) return `${PATH_HISTORY_LABEL}.pathSpeed`
    if (prevCount === 0 && prev.samples.length >= 2 && next.duration_ms < prev.duration_ms) {
      return `${PATH_HISTORY_LABEL}.pathTrim`
    }
    return `${PATH_HISTORY_LABEL}.pathRecord`
  }
  return `${PATH_HISTORY_LABEL}.pathMovePoint`
}

export function hasCameraPath(path: CameraPath | null | undefined): boolean {
  return (path?.samples?.length ?? 0) >= 2
}

function vecFinite(pos: PathVec3): boolean {
  return Number.isFinite(pos[0]) && Number.isFinite(pos[1]) && Number.isFinite(pos[2])
}

function stepMeters(a: PathVec3, b: PathVec3): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2])
}

function clipPositions<T>(items: readonly T[], posOf: (item: T) => PathVec3): T[] {
  const kept: T[] = []
  for (const item of items) {
    const pos = posOf(item)
    if (!vecFinite(pos)) break
    const prev = kept[kept.length - 1]
    if (prev && stepMeters(posOf(prev), pos) > MAX_PATH_STEP_M) break
    kept.push(item)
  }
  return kept
}

/** Префикс до первого невозможного кадра. Исходный путь не меняется; целый путь возвращается как есть. */
export function clipBrokenCameraPath(path: CameraPath): CameraPath {
  const samples = clipPositions(path.samples, (sample) => sample.pos)
  const rawPoints = path.authoring?.points ?? []
  const points = path.authoring ? clipPositions(rawPoints, (point) => point.pos) : rawPoints
  const samplesCut = samples.length !== path.samples.length
  const pointsCut = Boolean(path.authoring) && points.length !== rawPoints.length
  if (!samplesCut && !pointsCut) return path

  const samplesOut = samplesCut ? (samples.length >= 2 ? samples : []) : path.samples
  const authoring = !path.authoring
    ? undefined
    : pointsCut
      ? points.length >= 2
        ? { ...path.authoring, points }
        : undefined
      : path.authoring
  const lastKeptT = samplesOut[samplesOut.length - 1]?.t ?? 0
  const durationMs = samplesCut ? (samplesOut.length >= 2 ? lastKeptT : 0) : path.duration_ms
  const lookKeys = path.look_keys
    ? samplesCut
      ? path.look_keys.filter((key) => key.t <= lastKeptT + 1e-6)
      : path.look_keys
    : undefined
  const speed =
    path.speed && samplesCut ? (trimSpeedProfile(path.speed, path.duration_ms, 0, durationMs) ?? undefined) : path.speed
  return {
    version: path.version,
    duration_ms: durationMs,
    samples: samplesOut,
    ...(authoring ? { authoring } : {}),
    ...(lookKeys && lookKeys.length > 0 ? { look_keys: lookKeys.map((key) => ({ ...key })) } : {}),
    ...(speed ? { speed } : {}),
  }
}

export function cameraPathIsAuthored(path: CameraPath | null | undefined): boolean {
  return (path?.authoring?.points.length ?? 0) > 0
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

function lerpAngle(a: number, b: number, t: number) {
  let d = b - a
  while (d > Math.PI) d -= Math.PI * 2
  while (d < -Math.PI) d += Math.PI * 2
  return a + d * t
}

function lookKeyFinite(key: PathLookKey): boolean {
  return Number.isFinite(key.t) && Number.isFinite(key.yaw) && Number.isFinite(key.pitch)
}

export function normalizeLookKeys(raw: unknown): PathLookKey[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const keys: PathLookKey[] = []
  for (const item of raw) {
    if (keys.length >= MAX_PATH_SAMPLES) break
    if (!item || typeof item !== "object") continue
    const key = item as PathLookKey
    if (!lookKeyFinite(key)) continue
    keys.push({ t: key.t, yaw: key.yaw, pitch: key.pitch })
  }
  if (keys.length === 0) return undefined
  keys.sort((a, b) => a.t - b.t)
  return keys
}

const LOOK_UID_RE = /^[A-Za-z0-9_-]{1,64}$/

function pathVec3(raw: unknown): PathVec3 | null {
  if (!Array.isArray(raw) || raw.length !== 3) return null
  if (typeof raw[0] !== "number" || typeof raw[1] !== "number" || typeof raw[2] !== "number") return null
  if (!Number.isFinite(raw[0]) || !Number.isFinite(raw[1]) || !Number.isFinite(raw[2])) return null
  return [raw[0], raw[1], raw[2]]
}

function copyLookTarget(target: PathLookTarget): PathLookTarget {
  return {
    t_ms: target.t_ms,
    ...(target.object_uid ? { object_uid: target.object_uid } : {}),
    ...(target.point ? { point: [...target.point] as PathVec3 } : {}),
  }
}

export function clonePathLook(look: PathLook): PathLook {
  if (look.mode === "observe") return { mode: "observe", object_uid: look.object_uid }
  if (look.mode !== "recorded") return { mode: "tangent" }
  if (look.look_targets && look.look_targets.length > 0) {
    return { mode: "recorded", look_targets: look.look_targets.map(copyLookTarget) }
  }
  if (look.look_at) return { mode: "recorded", look_at: [...look.look_at] }
  return { mode: "recorded" }
}

function copyPathLook(look: PathLook): PathLook {
  return clonePathLook(look)
}

function normalizeLookTarget(raw: unknown): PathLookTarget | null {
  if (!raw || typeof raw !== "object") return null
  const row = raw as { t_ms?: unknown; object_uid?: unknown; point?: unknown }
  if (typeof row.t_ms !== "number" || !Number.isFinite(row.t_ms) || row.t_ms < 0) return null
  const uid = typeof row.object_uid === "string" && LOOK_UID_RE.test(row.object_uid) ? row.object_uid : undefined
  const point = pathVec3(row.point) ?? undefined
  if (!uid && !point) return null
  return {
    t_ms: Math.min(row.t_ms, MAX_PATH_DURATION_MS),
    ...(uid ? { object_uid: uid } : {}),
    ...(point ? { point } : {}),
  }
}

function normalizeLookTargets(raw: unknown): PathLookTarget[] | null {
  if (!Array.isArray(raw)) return null
  const targets: PathLookTarget[] = []
  for (const item of raw) {
    if (targets.length >= 64) break
    const target = normalizeLookTarget(item)
    if (target) targets.push(target)
  }
  if (targets.length === 0) return null
  targets.sort((a, b) => a.t_ms - b.t_ms)
  return targets
}

/** Режим взгляда из сохранённой сцены. Мусор и пустой список целей отбрасываются. */
export function normalizePathLook(raw: unknown): PathLook {
  if (!raw || typeof raw !== "object") return { mode: "tangent" }
  const look = raw as { mode?: unknown; object_uid?: unknown; look_at?: unknown; look_targets?: unknown }
  if (look.mode === "recorded") {
    const targets = normalizeLookTargets(look.look_targets)
    if (targets) return { mode: "recorded", look_targets: targets }
    const point = pathVec3(look.look_at)
    if (point) return { mode: "recorded", look_at: point }
    return { mode: "recorded" }
  }
  if (look.mode === "observe") {
    if (typeof look.object_uid === "string" && LOOK_UID_RE.test(look.object_uid)) {
      return { mode: "observe", object_uid: look.object_uid }
    }
    if (look.object_uid == null || look.object_uid === "") return { mode: "observe", object_uid: "" }
  }
  return { mode: "tangent" }
}

function cloneLookKeys(keys: PathLookKey[] | undefined): PathLookKey[] | undefined {
  if (!keys || keys.length === 0) return undefined
  return keys.map((key) => ({ t: key.t, yaw: key.yaw, pitch: key.pitch }))
}

export function scaleLookKeys(keys: readonly PathLookKey[], fromMs: number, toMs: number): PathLookKey[] {
  const scale = fromMs > 1e-6 && Number.isFinite(toMs) ? toMs / fromMs : 1
  return keys.map((key) => ({ t: key.t * scale, yaw: key.yaw, pitch: key.pitch }))
}

/** t_ms целей — место на траектории. Длительность двигает их вместе с пролётом. */
export function scaleLookTargets(look: PathLook, fromMs: number, toMs: number): PathLook {
  if (look.mode !== "recorded" || !look.look_targets || look.look_targets.length === 0) return look
  const scale = fromMs > 1e-6 && Number.isFinite(toMs) ? toMs / fromMs : 1
  if (scale === 1) return look
  return {
    mode: "recorded",
    look_targets: look.look_targets.map((target) => ({ ...copyLookTarget(target), t_ms: target.t_ms * scale })),
  }
}

function progressAtClock(speed: CameraSpeedProfile | null | undefined, duration: number, clock: number): number {
  if (!(duration > 0)) return 0
  if (speed) return pathProgress(speed, duration, clock)
  return Math.min(1, Math.max(0, clock / duration))
}

/**
 * Ключи в часах старого графика → часы нового.
 * Доля дуги считается по старому графику. Ровная скорость с обеих сторон растягивает время.
 */
export function remapLookKeys(
  keys: readonly PathLookKey[],
  fromSpeed: CameraSpeedProfile | null | undefined,
  fromMs: number,
  toSpeed: CameraSpeedProfile | null | undefined,
  toMs: number,
): PathLookKey[] {
  const fromDuration = Math.max(0, fromMs)
  const toDuration = Math.max(0, toMs)
  return keys.map((key) => ({
    t: clockAtArcFraction(toSpeed, toDuration, progressAtClock(fromSpeed, fromDuration, key.t)),
    yaw: key.yaw,
    pitch: key.pitch,
  }))
}

/**
 * Ключи пролёта лежат по доле дуги, не по часам.
 * Сначала старые часы этой доли, затем новые часы, затем снова доля — чтобы проигрывание не применило график дважды.
 */
export function remapStoredLookKeys(
  keys: readonly PathLookKey[],
  fromSpeed: CameraSpeedProfile | null | undefined,
  fromMs: number,
  toSpeed: CameraSpeedProfile | null | undefined,
  toMs: number,
): PathLookKey[] {
  if (!(fromMs > 1e-6) || !(toMs > 0)) return keys.map((key) => ({ ...key }))
  const fromDuration = Math.max(0, fromMs)
  const clocks = keys.map((key) => ({
    ...key,
    t: clockAtArcFraction(fromSpeed, fromDuration, key.t / fromDuration),
  }))
  const moved = remapLookKeys(clocks, fromSpeed, fromDuration, toSpeed, toMs)
  const carrier: CameraPath = {
    version: CAMERA_PATH_VERSION,
    duration_ms: Math.max(0, toMs),
    samples: [],
    ...(toSpeed ? { speed: toSpeed } : {}),
  }
  return moved.map((key) => ({ ...key, t: lookTimeMs(carrier, key.t) }))
}

function aimYawPitch(from: PathVec3, to: PathVec3): { yaw: number; pitch: number } {
  const dx = to[0] - from[0]
  const dy = to[1] - from[1]
  const dz = to[2] - from[2]
  const len = Math.hypot(dx, dy, dz)
  if (len < 1e-8) return { yaw: 0, pitch: 0 }
  const pitch = Math.asin(Math.min(1, Math.max(-1, dy / len)))
  const yaw = Math.atan2(-dx / len, -dz / len)
  return { yaw, pitch }
}

/** Точка взгляда едет по прямой между соседними ключами. До первого — первый, после последнего — последний. */
export function lookTargetPointAt(
  targets: readonly PathLookTarget[],
  timeMs: number,
  resolveObject: (uid: string) => PathVec3 | null,
): PathVec3 | null {
  if (targets.length === 0) return null
  const ordered = [...targets].sort((a, b) => a.t_ms - b.t_ms)
  const aimOf = (target: PathLookTarget): PathVec3 | null => {
    if (target.object_uid) {
      const live = resolveObject(target.object_uid)
      if (live) return live
    }
    return target.point ? ([...target.point] as PathVec3) : null
  }
  const first = ordered[0]
  if (ordered.length === 1 || timeMs <= first.t_ms) return aimOf(first)
  const last = ordered[ordered.length - 1]
  if (timeMs >= last.t_ms) return aimOf(last)

  let index = ordered.findIndex((target) => target.t_ms >= timeMs)
  if (index < 1) index = 1
  const left = aimOf(ordered[index - 1])
  const right = aimOf(ordered[index])
  if (!left) return right
  if (!right) return left
  const span = ordered[index].t_ms - ordered[index - 1].t_ms
  const share = span <= 0 ? 0 : (timeMs - ordered[index - 1].t_ms) / span
  return [lerp(left[0], right[0], share), lerp(left[1], right[1], share), lerp(left[2], right[2], share)]
}

function recordedAimPoint(
  look: PathLook,
  timeMs: number,
  resolveObject: (uid: string) => PathVec3 | null,
): PathVec3 | null {
  if (look.mode !== "recorded") return null
  if (look.look_targets && look.look_targets.length > 0)
    return lookTargetPointAt(look.look_targets, timeMs, resolveObject)
  return look.look_at ? ([...look.look_at] as PathVec3) : null
}

export function recordedLookHasTarget(look: PathLook): boolean {
  if (look.mode !== "recorded") return false
  return Boolean(look.look_at || (look.look_targets && look.look_targets.length > 0))
}

/** Поворот кадра в цель. aimTimeMs — место на траектории. null — точки нет. */
export function aimSampleAtLook(
  sample: CameraPathSample,
  look: PathLook,
  resolveObject: (uid: string) => PathVec3 | null,
  aimTimeMs: number = sample.t,
): CameraPathSample | null {
  const point = recordedAimPoint(look, aimTimeMs, resolveObject)
  if (!point) return null
  const yp = aimYawPitch(sample.pos, point)
  return { ...sample, yaw: yp.yaw, pitch: yp.pitch }
}

/** Кадры и ключи по доле дуги смотрят в цель из новой позиции. null — цели нет. */
export function aimSamplesAtLook(
  samples: readonly CameraPathSample[],
  look: PathLook,
  durationMs: number,
  speed: CameraSpeedProfile | null | undefined,
  resolveObject: (uid: string) => PathVec3 | null,
): { samples: CameraPathSample[]; keys: PathLookKey[] } | null {
  if (!recordedLookHasTarget(look)) return null
  const carrier: CameraPath = {
    version: CAMERA_PATH_VERSION,
    duration_ms: durationMs,
    samples: [],
    ...(speed ? { speed } : {}),
  }
  const keys: PathLookKey[] = []
  const aimed = samples.map((sample) => {
    const aimTime = lookTimeMs(carrier, sample.t)
    const next = aimSampleAtLook(sample, look, resolveObject, aimTime)
    if (!next) return sample
    keys.push({ t: aimTime, yaw: next.yaw, pitch: next.pitch })
    return next
  })
  if (keys.length === 0) return null
  return { samples: aimed, keys }
}

export function sampleLookKeys(keys: readonly PathLookKey[], timeMs: number): { yaw: number; pitch: number } | null {
  if (keys.length === 0) return null
  if (keys.length === 1 || timeMs <= keys[0].t) return { yaw: keys[0].yaw, pitch: keys[0].pitch }
  const last = keys[keys.length - 1]
  if (timeMs >= last.t) return { yaw: last.yaw, pitch: last.pitch }
  let i = 1
  while (i < keys.length && keys[i].t < timeMs) i += 1
  const a = keys[i - 1]
  const b = keys[i]
  const span = b.t - a.t
  const u = span <= 0 ? 0 : (timeMs - a.t) / span
  return { yaw: lerpAngle(a.yaw, b.yaw, u), pitch: lerp(a.pitch, b.pitch, u) }
}

/**
 * Ключи остаются на пролёте. В кадры попадают только в режиме «Взгляд».
 * «Вдоль» и «Наблюдение» yaw кадров не трогают.
 */
export function attachLookKeys(path: CameraPath, keys: readonly PathLookKey[] | undefined): CameraPath {
  const lookKeys = normalizeLookKeys(keys)
  if (!lookKeys) {
    if (!path.look_keys) return path
    const { look_keys: _dropped, ...rest } = path
    return rest
  }
  if (path.authoring?.look.mode === "recorded") return applyLookKeys(path, lookKeys)
  return { ...path, look_keys: lookKeys }
}

/** Подменяет yaw/pitch кадров дорожкой. Позиции и fov не трогает. */
export function applyLookKeys(path: CameraPath, keys: readonly PathLookKey[]): CameraPath {
  const lookKeys = normalizeLookKeys(keys)
  if (!lookKeys) {
    const { look_keys: _dropped, ...rest } = path
    return rest
  }
  return {
    ...path,
    look_keys: lookKeys,
    samples: path.samples.map((sample) => {
      const look = sampleLookKeys(lookKeys, lookTimeMs(path, sample.t))
      if (!look) return sample
      return { ...sample, yaw: look.yaw, pitch: look.pitch }
    }),
  }
}

/** Заменяет ключи на отрезке [fromMs, toMs]. Хвост после остановки остаётся. */
export function replaceLookRange(
  base: readonly PathLookKey[],
  recorded: readonly PathLookKey[],
  fromMs: number,
  toMs: number,
): PathLookKey[] {
  const from = Math.min(fromMs, toMs)
  const to = Math.max(fromMs, toMs)
  const head = base.filter((key) => key.t < from - 1e-3)
  const tail = base.filter((key) => key.t > to + 1e-3)
  const mid = recorded.filter((key) => key.t >= from - 1e-3 && key.t <= to + 1e-3)
  return [...head, ...mid, ...tail].map((key) => ({ t: key.t, yaw: key.yaw, pitch: key.pitch }))
}

export function appendCameraSample(
  path: CameraPath,
  sample: Omit<CameraPathSample, "t">,
  elapsedMs: number,
): CameraPath {
  if (path.samples.length >= MAX_PATH_SAMPLES) return path
  if (elapsedMs > MAX_PATH_DURATION_MS) return path
  const last = path.samples[path.samples.length - 1]
  if (last && elapsedMs - last.t < SAMPLE_INTERVAL_MS) return path
  return {
    version: 1,
    duration_ms: Math.min(elapsedMs, MAX_PATH_DURATION_MS),
    samples: [...path.samples, { ...sample, t: elapsedMs }],
  }
}

export function cameraPathDurationMs(path: CameraPath): number {
  if (path.duration_ms > 0) return path.duration_ms
  return path.samples[path.samples.length - 1]?.t ?? 0
}

/** Часы ролика → время ключей взгляда. Без графика доля равна доле часов. */
export function lookTimeMs(path: CameraPath, clockMs: number): number {
  const duration = cameraPathDurationMs(path)
  if (!(duration > 0)) return clockMs
  const clock = Math.max(0, Math.min(clockMs, duration))
  const fraction = path.speed ? pathProgress(path.speed, duration, clock) : clock / duration
  return fraction * duration
}

/** Кадры с тем же шагом, что и выпечка сплайна. Последняя метка — конец ролика. */
export function pathSampleTimes(duration: number): number[] {
  if (!(duration > 0)) return [0]
  const countAtInterval = Math.floor(duration / SAMPLE_INTERVAL_MS) + 1
  const step = countAtInterval <= MAX_PATH_SAMPLES ? SAMPLE_INTERVAL_MS : Math.ceil(duration / (MAX_PATH_SAMPLES - 1))
  const times: number[] = []
  for (let t = 0; t <= duration && times.length < MAX_PATH_SAMPLES; t += step) times.push(t)
  const last = times[times.length - 1] ?? 0
  if (last < duration && times.length < MAX_PATH_SAMPLES) times.push(duration)
  return times
}

/**
 * Обрезка пути до [inMs, outMs]: граничные сэмплы интерполируются точно на границах,
 * внутренние сохраняются, время ребейзится к нулю.
 */
export function trimCameraPath(path: CameraPath, inMs: number, outMs: number): CameraPath {
  const duration = cameraPathDurationMs(path)
  const from = Math.max(0, Math.min(inMs, duration))
  const to = Math.max(from, Math.min(outMs, duration))
  if (path.samples.length < 2 || to - from < MIN_TRIM_SPAN_MS) return path
  if (from <= 0 && to >= duration) return path

  const first = interpolateCameraPath(path, from)
  const last = interpolateCameraPath(path, to)
  if (!first || !last) return path

  const inner = path.samples.filter((s) => s.t > from && s.t < to)
  const samples = [{ ...first, t: 0 }, ...inner.map((s) => ({ ...s, t: s.t - from })), { ...last, t: to - from }]
  const speed = path.speed ? (trimSpeedProfile(path.speed, duration, from, to) ?? undefined) : undefined
  return { version: 1, duration_ms: to - from, samples, ...(speed ? { speed } : {}) }
}

export function interpolateCameraPath(path: CameraPath, timeMs: number): CameraPathSample | null {
  const samples = path.samples
  if (samples.length === 0) return null
  if (samples.length === 1) return { ...samples[0] }

  const duration = path.duration_ms > 0 ? path.duration_ms : samples[samples.length - 1].t
  const tMs = Math.max(0, Math.min(timeMs, duration))
  if (tMs <= samples[0].t) return { ...samples[0] }
  const last = samples[samples.length - 1]
  if (tMs >= last.t) return { ...last }

  let i = 1
  while (i < samples.length && samples[i].t < tMs) i += 1
  const a = samples[i - 1]
  const b = samples[i]
  const span = b.t - a.t
  const u = span <= 0 ? 0 : (tMs - a.t) / span

  return {
    t: tMs,
    pos: [lerp(a.pos[0], b.pos[0], u), lerp(a.pos[1], b.pos[1], u), lerp(a.pos[2], b.pos[2], u)],
    yaw: lerpAngle(a.yaw, b.yaw, u),
    pitch: lerp(a.pitch, b.pitch, u),
    fov: lerp(a.fov, b.fov, u),
  }
}

type ArcPose = { dist: number; sample: CameraPathSample }

function arcPoses(samples: readonly CameraPathSample[]): ArcPose[] {
  let dist = 0
  return samples.map((sample, index) => {
    if (index > 0) {
      const prev = samples[index - 1].pos
      dist += Math.hypot(sample.pos[0] - prev[0], sample.pos[1] - prev[1], sample.pos[2] - prev[2])
    }
    return { dist, sample }
  })
}

function sampleArc(table: readonly ArcPose[], distance: number): CameraPathSample {
  const first = table[0]
  if (!first) {
    return { t: 0, pos: [0, 0, 0], yaw: 0, pitch: 0, fov: 55 }
  }
  if (distance <= 0) return { ...first.sample, pos: [...first.sample.pos] as PathVec3 }
  const last = table[table.length - 1]
  if (distance >= last.dist) return { ...last.sample, pos: [...last.sample.pos] as PathVec3 }
  let lo = 0
  let hi = table.length - 1
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1
    if (table[mid].dist < distance) lo = mid
    else hi = mid
  }
  const a = table[lo]
  const b = table[hi]
  const span = b.dist - a.dist
  const u = span <= 1e-8 ? 0 : (distance - a.dist) / span
  return {
    t: 0,
    pos: [
      lerp(a.sample.pos[0], b.sample.pos[0], u),
      lerp(a.sample.pos[1], b.sample.pos[1], u),
      lerp(a.sample.pos[2], b.sample.pos[2], u),
    ],
    yaw: lerpAngle(a.sample.yaw, b.sample.yaw, u),
    pitch: lerp(a.sample.pitch, b.sample.pitch, u),
    fov: lerp(a.sample.fov, b.sample.fov, u),
  }
}

function withoutSpeed(path: CameraPath): CameraPath {
  if (!path.speed) return path
  const { speed: _speed, ...rest } = path
  return rest
}

/**
 * Пересобирает кадры записи по доле дуги. Ключи взгляда остаются на доле пути
 * и заново ложатся на кадры. Без профиля — равномерно по длине.
 */
export function retimeCameraPath(path: CameraPath, speed: CameraSpeedProfile | null): CameraPath {
  const duration = cameraPathDurationMs(path)
  const normalized = speed ? normalizeSpeedProfile(speed, duration) : null
  if (path.samples.length < 2 || !(duration > 0)) {
    if (!normalized) return withoutSpeed(path)
    return { ...path, speed: normalized }
  }
  const table = arcPoses(path.samples)
  const total = table[table.length - 1]?.dist ?? 0
  const samples = pathSampleTimes(duration).map((t) => {
    const fraction = normalized ? pathProgress(normalized, duration, t) : t / duration
    const pose = sampleArc(table, total * fraction)
    return { ...pose, t }
  })
  const next: CameraPath = {
    version: path.version,
    duration_ms: duration,
    samples,
    ...(path.authoring ? { authoring: path.authoring } : {}),
    ...(normalized ? { speed: normalized } : {}),
  }
  const aimed = path.authoring ? aimSamplesAtLook(samples, path.authoring.look, duration, normalized, () => null) : null
  if (aimed) {
    return {
      ...next,
      samples: aimed.samples,
      look_keys: aimed.keys,
    }
  }
  const keys = path.look_keys?.length
    ? remapStoredLookKeys(path.look_keys, path.speed, duration, normalized, duration)
    : undefined
  if (normalized && path.authoring?.look.mode === "recorded" && keys && keys.length > 0) {
    return applyLookKeys(next, keys)
  }
  return {
    ...next,
    ...(keys && keys.length > 0 ? { look_keys: keys } : {}),
  }
}
